import React, {useContext, useState} from 'react';
import {Location} from "history";
import {Alert} from 'react-bootstrap';
import {getQueryParam} from '../../utils/location-utils';
import ManageSchema from "./ManageSchema";

import ActivateSchemaAction from "../../components/schema/actions/ActivateSchemaAction";
import DeleteSchemaAction from "../../components/schema/actions/DeleteSchemaAction";
import OpenSchemaAction from "../../components/schema/actions/OpenSchemaAction";
import {SchemaContext, SchemaContextProvider} from "../../components/schema/context/SchemaContext";
import DownloadSchemaAction from "../../components/schema/actions/DownloadSchemaAction";
import ContentBlocker from "../../components/context/ContentBlocker";
import UploadSchemaAction from "../../components/schema/actions/UploadSchemaAction";
import Loader from "../../components/loader/Loader";
import ValidateDataAction from "../../components/schema/actions/ValidateDataAction";

import './ManageSchemaPage.scss';

type ManageSchemaPageProps = {
  location: Location;
  history: any;
}

class ManageSchemaPage extends React.Component<ManageSchemaPageProps> {

  private readonly schemaId: string;

  constructor(props: any) {
    super(props);
    this.schemaId = this.getSchemaId();
  }

  getSchemaId() {
    return getQueryParam(this.props.location, 'id') as string;
  }

  onActivateSchema = () => {
    // Forces reloading of the schema page
    this.props.history.push({
      search: `?id=${this.schemaId}`
    });
  };

  onDeleteSchema = () => {
    this.props.history.push('/schemas');
  };

  render() {
    return (
      <ContentBlocker checkReadPermission={true}>
        <SchemaContextProvider schemaId={this.schemaId}>
          <div className="ManageSchemaPage">
            <h1>Schema {this.schemaId}</h1>
            <ManageSchemaWrapper>
              <ManageSchemaActions schemaId={this.schemaId}
                                        onActivateSchema={this.onActivateSchema}
                                        onDeleteSchema={this.onDeleteSchema}/>
              <ManageSchema schemaId={this.schemaId}/>
            </ManageSchemaWrapper>
          </div>
        </SchemaContextProvider>
      </ContentBlocker>
    );
  }
}

/**
 * Renders the provided nested components or an alert if the schema is missing or cannot be loaded.
 */
function ManageSchemaWrapper(props: { children: any }) {
  const schemaContext = useContext(SchemaContext);
  if (!schemaContext.loading && schemaContext.error) {
    return <Alert variant="danger" className="ManageSchemaAlert">{schemaContext.error}</Alert>;
  } else {
    return props.children;
  }
}

function ManageSchemaActions(props: {
  schemaId: string,
  onActivateSchema: () => void,
  onDeleteSchema: () => void
}) {
  const [executingAction, setExecutingAction] = useState(false);
  const schema = useContext(SchemaContext);
  const permissions = schema.permissions;

  const onBeginActivate = () => setExecutingAction(true);
  const onActivate = () => {
    setExecutingAction(false);
    props.onActivateSchema();
  };
  const onFailedActivate = () => setExecutingAction(false);

  return (
    <>
      <OpenSchemaAction schemaId={props.schemaId}
                        standardButton={true}
                        disabled={executingAction}/>
      <UploadSchemaAction schemaId={props.schemaId}
                          standardButton={true}
                          disabled={executingAction}
                          forbidden={!permissions.canWrite}/>
      <DownloadSchemaAction schemaId={props.schemaId}
                            standardButton={true}
                            disabled={executingAction}/>
      <ValidateDataAction schemaId={props.schemaId}
                            standardButton={true}
                            disabled={executingAction}/>
      {
        !schema.isBound ? <>
          <ActivateSchemaAction schemaId={props.schemaId}
                                disabled={executingAction}
                                forbidden={!permissions.canBind}
                                onBeginActivate={onBeginActivate}
                                onActivate={onActivate}
                                onFailedActivate={onFailedActivate}
                                standardButton={true}/>
          <DeleteSchemaAction schemaId={props.schemaId}
                              disabled={executingAction}
                              forbidden={!permissions.canDelete}
                              onDelete={props.onDeleteSchema}
                              standardButton={true}/>
        </> : null
      }
      {
        executingAction ? <Loader/> : null
      }
    </>
  );
}

export default ManageSchemaPage;
